/**
 * Web shim for `react-native-keychain`.
 *
 * The real module is native-only and crashes under react-native-web.
 * We provide a minimal compatible implementation backed by `localStorage`.
 */

type KeychainOptions = {
  service?: string;
};

export type UserCredentials = {
  username: string;
  password: string;
  service?: string;
};

const storageKeyForService = (service?: string) =>
  `bizplus.keychain.${service || 'default'}`;

function hasStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

export async function setGenericPassword(
  username: string,
  password: string,
  options?: KeychainOptions,
): Promise<boolean> {
  if (!hasStorage()) return false;
  const key = storageKeyForService(options?.service);
  const value: UserCredentials = {
    username,
    password,
    service: options?.service,
  };
  window.localStorage.setItem(key, JSON.stringify(value));
  return true;
}

export async function getGenericPassword(
  options?: KeychainOptions,
): Promise<false | UserCredentials> {
  if (!hasStorage()) return false;
  const key = storageKeyForService(options?.service);
  const raw = window.localStorage.getItem(key);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as Partial<UserCredentials>;
    if (!parsed || typeof parsed.password !== 'string') return false;
    return {
      username: String(parsed.username || ''),
      password: String(parsed.password || ''),
      service: options?.service,
    };
  } catch {
    return false;
  }
}

export async function resetGenericPassword(
  options?: KeychainOptions,
): Promise<boolean> {
  if (!hasStorage()) return false;
  const key = storageKeyForService(options?.service);
  window.localStorage.removeItem(key);
  return true;
}
